import React, { useCallback } from 'react';
import { RouteComponentProps } from 'react-router';
import Loading from 'components/base/loading/loading';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { Trans, t } from '@lingui/macro';
import { TransEnum } from 'components/base/i18n/TransEnum';
import { Enum } from 'util/backendapi/models/api.interfaces';
import {
  AlertWarning,
  AlertInfo,
  AlertDanger,
} from 'components/base/alert/alert';
import { formatDatetimeForDisplay } from 'util/dates';
import { HasPermission } from 'components/logic/has-permission/HasPermission';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';
import { BackButton } from 'components/base/back-button/BackButton';
import orderBy from 'lodash/orderBy';
import { useGetApi } from 'hooks/use-get-api';
import { deleteApi } from 'util/backendapi/fetch';
import { errorToString } from 'util/backendapi/error';
import { ObservationPointDecorated } from 'util/backendapi/types/Model';

interface Props
  extends RouteComponentProps<{
    obsPointCode: string;
    fieldName: Enum.ObservationPoint_TIME_DEPENDENT_FIELD_NAME;
  }> {}

export function ObsPointTimeDependentFieldsScreen(props: Props) {
  const { obsPointCode, fieldName } = props.match.params;

  const [{ isLoading, error, data }, , , reloadObsPoint] = useGetApi(
    '/observation-points/',
    {
      fields: ['code', 'observation_point_time_dependent_fields', 'time_zone'],
      code: obsPointCode,
    }
  );

  const observationPoint:
    | Pick<
        ObservationPointDecorated,
        'code' | 'observation_point_time_dependent_fields' | 'time_zone'
      >
    | null
    | undefined = data ? (data.length === 0 ? null : data[0]) : undefined;

  return (
    <ObsPointTimeDependentFieldsScreenView
      obsPointCode={obsPointCode}
      fieldName={fieldName}
      observationPoint={observationPoint}
      isLoading={
        isLoading || !data || (data && observationPoint?.code !== obsPointCode)
      }
      errorMessage={error}
      reloadObsPoint={reloadObsPoint}
    />
  );
}

export function ObsPointTimeDependentFieldsScreenView(props: {
  obsPointCode: string;
  fieldName: Enum.ObservationPoint_TIME_DEPENDENT_FIELD_NAME;
  isLoading: boolean;
  errorMessage: any;
  observationPoint:
    | Pick<
        ObservationPointDecorated,
        'code' | 'observation_point_time_dependent_fields' | 'time_zone'
      >
    | undefined
    | null;
  reloadObsPoint: () => void;
}) {
  const {
    observationPoint,
    obsPointCode,
    fieldName,
    isLoading,
    errorMessage,
    reloadObsPoint,
  } = props;

  // Whitelist the field name param
  const isValidFieldName = Object.values(
    Enum.ObservationPoint_TIME_DEPENDENT_FIELD_NAME
  ).includes(fieldName);

  const fieldEntries = isValidFieldName
    ? orderBy(
        observationPoint?.observation_point_time_dependent_fields[fieldName] ??
          [],
        [(e) => e.start_datetime],
        ['desc']
      )
    : [];

  // A tiny React component to curry `<TransEnum>`, since we have to call it
  // several times with the same props.
  const MyFieldName = useCallback(
    () =>
      isValidFieldName ? (
        <TransEnum
          enum="ObservationPoint_TIME_DEPENDENT_FIELD_NAME"
          value={fieldName}
        />
      ) : (
        <>{fieldName}</>
      ),
    [fieldName, isValidFieldName]
  );

  return (
    <PageStandard
      name="observation-point-time-dependent-fields"
      header={
        <Trans>
          <MyFieldName /> for {obsPointCode}
        </Trans>
      }
    >
      <div className="page-content-header-with-back-button-wrapper">
        <BackButton
          title={t`Back to observation point`}
          defaultBackUrl={`/observation-point/${obsPointCode}`}
        />
      </div>

      {!isValidFieldName ? (
        <AlertDanger>
          <Trans>"{fieldName}" is not a valid field name.</Trans>
        </AlertDanger>
      ) : errorMessage ? (
        <AlertWarning>{errorToString(errorMessage)}</AlertWarning>
      ) : observationPoint === null ? (
        <AlertWarning>Observation point {obsPointCode} not found.</AlertWarning>
      ) : isLoading || !observationPoint ? (
        <Loading />
      ) : fieldEntries.length === 0 ? (
        <AlertInfo>
          <Trans>
            Observation point {obsPointCode} has no existing <MyFieldName />{' '}
            records.
          </Trans>
        </AlertInfo>
      ) : (
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>
                  <Trans>Field</Trans>
                </th>
                <th>
                  <Trans>Start Date</Trans>
                </th>
                <th>
                  <Trans>Reason for change</Trans>
                </th>
                <th>
                  <Trans>Value</Trans>
                </th>
                <th className="action-icons">
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {fieldEntries.map(
                ({
                  id: fieldEntryId,
                  start_datetime,
                  change_reason,
                  value,
                }) => (
                  <tr key={fieldEntryId}>
                    <td>
                      <MyFieldName />
                    </td>
                    <td>
                      {formatDatetimeForDisplay(
                        start_datetime,
                        observationPoint.time_zone.name
                      )}
                    </td>
                    <td>{change_reason}</td>
                    <td>{value}</td>
                    <td className="action-icons">
                      <HasPermission check="can_delete_observation_point_time_dependent_fields">
                        <ButtonShowConfirmation
                          name="delete-obs-point-time-dependent-field"
                          title={t`Delete`}
                          iconType="icon-circle-minus"
                          className="btn-link-panel"
                          content={
                            <Trans>
                              Are you sure you want to delete this{' '}
                              <strong>
                                <MyFieldName />
                              </strong>{' '}
                              record? This action is not reversible.
                            </Trans>
                          }
                          okBtnText={<Trans>Yes, delete</Trans>}
                          onConfirm={async () => {
                            await deleteApi(
                              `/observation-point-time-dependent-fields/${fieldEntryId}/`
                            );
                            reloadObsPoint();
                          }}
                        />
                      </HasPermission>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageStandard>
  );
}
